import React, { useState, useEffect } from 'react';
import { useChat } from '../../../hooks/useChat';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const propertyData = {
  address: '42 Harbour View Terrace, Mosman NSW 2088',
  listingPrice: 2800000,
  bedrooms: 4,
  bathrooms: 3,
  parking: 2,
  daysOnMarket: 47,
  suburbMedianDays: 32,
  suburb: 'Mosman',
  state: 'NSW',
  riskScore: 42,
  riskLevel: 'Medium',
  flaggedRisks: [
    'Extended days on market (47 vs 32 median)',
    'Strata levy arrears reported',
    'Proximity to flight path — noise zone B',
    'Pending council development application nearby',
  ],
  propertyAge: 1985,
  propertyType: 'House',
  priceGrowth12m: 8.4,
};

const SYSTEM_PROMPT = `You are a senior Australian property risk analyst specialising in due diligence, risk assessment, and buyer protection. Provide detailed, actionable risk analysis in structured JSON format. Be specific about Australian property law, strata regulations, and market conditions.`;

function buildPrompt(data) {
  return `Perform a comprehensive risk assessment for this Australian property:

Property: ${data.address}
Listing Price: $${data.listingPrice.toLocaleString()}
Property Type: ${data.propertyType} | Built: ${data.propertyAge}
Bedrooms: ${data.bedrooms} | Bathrooms: ${data.bathrooms}
Days on Market: ${data.daysOnMarket} (suburb median: ${data.suburbMedianDays})
Risk Score: ${data.riskScore}/100 (${data.riskLevel} Risk)
Suburb: ${data.suburb}, ${data.state}
12-Month Price Growth: ${data.priceGrowth12m}%

Flagged Risks:
${data.flaggedRisks.map((r, i) => `${i + 1}. ${r}`).join('\n')}

Provide a JSON response with exactly this structure:
{
  "riskSummary": {
    "overallRating": "Low | Medium | High | Critical",
    "headline": "One sentence risk headline",
    "summary": "3-4 sentence comprehensive risk overview",
    "proceedRecommendation": "Proceed with Caution | Proceed | Do Not Proceed"
  },
  "riskFactors": [
    {
      "category": "Market Risk | Legal Risk | Structural Risk | Financial Risk | Environmental Risk",
      "title": "Risk title",
      "severity": "High | Medium | Low",
      "description": "2 sentence description",
      "impact": "Potential financial or legal impact"
    }
  ],
  "mitigationStrategies": [
    {
      "risk": "Risk being mitigated",
      "action": "Specific action to take",
      "priority": "Immediate | Before Exchange | Before Settlement",
      "estimatedCost": "$X,XXX or Free"
    }
  ],
  "offerRecommendation": {
    "strategy": "Aggressive | Conservative | Standard",
    "minimumOffer": "$X,XXX,XXX",
    "maximumOffer": "$X,XXX,XXX",
    "riskAdjustedDiscount": "X%",
    "conditions": ["condition1", "condition2", "condition3"],
    "rationale": "2-3 sentence explanation of offer strategy based on risks"
  }
}`;
}

const severityColor = (s) => {
  if (!s) return '#FFAA00';
  if (s === 'High') return '#FF6666';
  if (s === 'Low') return '#00FF88';
  return '#FFAA00';
};

const priorityColor = (p) => {
  if (!p) return '#FFAA00';
  if (p === 'Immediate') return '#FF6666';
  if (p === 'Before Exchange') return '#FFAA00';
  return '#00D4FF';
};

const AIRiskSummary = () => {
  const [analysis, setAnalysis] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (error) toast.error('Failed to generate AI risk analysis. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const cleaned = response.replace(/```json\n?|```\n?/g, '').trim();
        const parsed = JSON.parse(cleaned);
        setAnalysis(parsed);
      } catch {
        toast.error('Could not parse AI response. Please regenerate.');
      }
    }
  }, [response, isLoading]);

  const handleGenerate = () => {
    setAnalysis(null);
    setHasGenerated(true);
    sendMessage([
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user', content: buildPrompt(propertyData) }
    ], { max_completion_tokens: 1500 });
  };

  const proceedColor = (rec) => {
    if (!rec) return '#FFAA00';
    if (rec?.includes('Do Not')) return '#FF6666';
    if (rec === 'Proceed') return '#00FF88';
    return '#FFAA00';
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,68,68,0.15)', border: '1px solid rgba(255,68,68,0.3)' }}>
            <Icon name="BrainCircuit" size={18} color="#FF6666" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">AI Risk Analysis</h3>
            <p className="text-xs text-muted-foreground">Powered by OpenAI GPT-5</p>
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-60"
          style={{ background: isLoading ? 'rgba(255,68,68,0.05)' : 'rgba(255,68,68,0.12)', color: '#FF6666', border: '1px solid rgba(255,68,68,0.3)' }}
        >
          <Icon name={isLoading ? 'Loader' : 'Sparkles'} size={14} color="#FF6666" className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Analysing...' : hasGenerated ? 'Regenerate' : 'Generate Risk Analysis'}
        </button>
      </div>

      {!hasGenerated && !isLoading && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.2)' }}>
            <Icon name="ShieldAlert" size={24} color="#FF6666" />
          </div>
          <p className="text-foreground font-medium mb-1">Generate AI-Powered Risk Analysis</p>
          <p className="text-sm text-muted-foreground max-w-sm">Click "Generate Risk Analysis" to get a comprehensive risk summary, factor analysis, mitigation strategies, and offer recommendations powered by OpenAI.</p>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-muted rounded w-full mb-1"></div>
              <div className="h-3 bg-muted rounded w-4/5"></div>
            </div>
          ))}
        </div>
      )}

      {analysis && !isLoading && (
        <div className="space-y-5">
          {/* Risk Summary */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(255,68,68,0.05)', border: '1px solid rgba(255,68,68,0.15)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="AlertTriangle" size={15} color="#FF6666" />
              <span className="text-sm font-semibold text-foreground">Risk Summary</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `rgba(${proceedColor(analysis?.riskSummary?.proceedRecommendation) === '#FF6666' ? '255,68,68' : proceedColor(analysis?.riskSummary?.proceedRecommendation) === '#00FF88' ? '0,255,136' : '255,170,0'},0.15)`, color: proceedColor(analysis?.riskSummary?.proceedRecommendation) }}>{analysis?.riskSummary?.proceedRecommendation}</span>
            </div>
            <p className="text-sm font-medium text-foreground mb-2">{analysis?.riskSummary?.headline}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{analysis?.riskSummary?.summary}</p>
          </div>

          {/* Risk Factors */}
          {analysis?.riskFactors?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="ListChecks" size={15} color="#FFAA00" />
                <span className="text-sm font-semibold text-foreground">Risk Factor Analysis</span>
              </div>
              <div className="space-y-2">
                {analysis?.riskFactors?.map((rf, i) => (
                  <div key={i} className="rounded-lg p-3" style={{ background: 'rgba(255,170,0,0.05)', border: '1px solid rgba(255,170,0,0.12)' }}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,170,0,0.15)', color: '#FFAA00' }}>{rf?.category}</span>
                        <span className="text-sm font-medium text-foreground">{rf?.title}</span>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full shrink-0" style={{ background: `rgba(${severityColor(rf?.severity) === '#FF6666' ? '255,102,102' : severityColor(rf?.severity) === '#00FF88' ? '0,255,136' : '255,170,0'},0.15)`, color: severityColor(rf?.severity) }}>{rf?.severity}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{rf?.description}</p>
                    <p className="text-xs" style={{ color: '#FFAA00' }}>Impact: {rf?.impact}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mitigation Strategies */}
          {analysis?.mitigationStrategies?.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="ShieldCheck" size={15} color="#00D4FF" />
                <span className="text-sm font-semibold text-foreground">Mitigation Strategies</span>
              </div>
              <div className="space-y-2">
                {analysis?.mitigationStrategies?.map((ms, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(0,212,255,0.15)' }}>
                      <span className="text-xs font-bold" style={{ color: '#00D4FF' }}>{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-foreground">{ms?.action}</p>
                        <span className="text-xs px-1.5 py-0.5 rounded shrink-0" style={{ background: `rgba(${priorityColor(ms?.priority) === '#FF6666' ? '255,102,102' : priorityColor(ms?.priority) === '#FFAA00' ? '255,170,0' : '0,212,255'},0.12)`, color: priorityColor(ms?.priority) }}>{ms?.priority}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{ms?.risk} · Est. cost: {ms?.estimatedCost}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Offer Recommendation */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.15)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="HandCoins" size={15} color="#00FF88" />
              <span className="text-sm font-semibold text-foreground">Offer Recommendation</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,255,136,0.15)', color: '#00FF88' }}>{analysis?.offerRecommendation?.strategy} Strategy</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="rounded-lg p-3" style={{ background: 'rgba(0,255,136,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">Min Offer</p>
                <p className="text-sm font-bold" style={{ color: '#00FF88' }}>{analysis?.offerRecommendation?.minimumOffer}</p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'rgba(0,255,136,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">Max Offer</p>
                <p className="text-sm font-bold" style={{ color: '#00FF88' }}>{analysis?.offerRecommendation?.maximumOffer}</p>
              </div>
              <div className="rounded-lg p-3" style={{ background: 'rgba(255,68,68,0.08)' }}>
                <p className="text-xs text-muted-foreground mb-1">Risk Discount</p>
                <p className="text-sm font-bold" style={{ color: '#FF6666' }}>{analysis?.offerRecommendation?.riskAdjustedDiscount}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">{analysis?.offerRecommendation?.rationale}</p>
            {analysis?.offerRecommendation?.conditions?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-foreground mb-2">Recommended Conditions:</p>
                <div className="flex flex-wrap gap-2">
                  {analysis?.offerRecommendation?.conditions?.map((c, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(0,255,136,0.08)', color: '#00FF88', border: '1px solid rgba(0,255,136,0.2)' }}>{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIRiskSummary;
